/**
 * Handoff-alert SSE stream lifecycle (SPEC-2157 / SPEC-2221).
 *
 * Extracted from handoff.ts as a dependency-injected, env-free unit so the
 * auth-sensitive connection lifecycle can be unit-tested without pulling in the
 * axios/firebase/Vite-env graph. `subscribeToHandoffAlerts` in handoff.ts wires
 * the real mint request + base URL into `openHandoffAlertStream`.
 */
import { Handoff, HandoffSSEEvent, HandoffEventType } from '../types/handoff.types';

export interface RawSSEHandoff {
  alert_type: string;
  handoff_id: string;
  organization_id: string;
  contact_id: string;
  contact_name: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  hotness_score: number;
  hotness_level: string;
  signals: {
    buyingIntent: boolean;
    urgency: boolean;
    tour: boolean;
    callback?: boolean;
    agentRequest?: boolean;
  };
  reasons: string[];
  status: string;
  claim_expires_at: string;
  assigned_agent_id: string | null;
  assigned_agent_name?: string | null;
  outcome: string | null;
  sentiment: string | null;
  strategy_type: string | null;
  conversation_summary: string | null;
  timestamp: string;
  claimed_at?: string | null;
  source?: string;
}

/**
 * Transform SSE raw data (snake_case) to frontend format (camelCase)
 */
export const transformSSEToHandoff = (raw: RawSSEHandoff): HandoffSSEEvent => {
  // Map alert_type to HandoffEventType
  const typeMap: Record<string, HandoffEventType> = {
    new_handoff: 'new_handoff',
    claimed: 'claimed',
    declined: 'declined',
    expired: 'expired',
  };

  const handoff: Handoff = {
    handoffId: raw.handoff_id,
    organizationId: raw.organization_id,
    contactId: raw.contact_id,
    contactName: raw.contact_name,
    contactPhone: raw.contact_phone || null,
    contactEmail: raw.contact_email || null,
    source: (raw.source as Handoff['source']) || 'followup_feature',
    hotnessScore: raw.hotness_score,
    hotnessLevel: raw.hotness_level as Handoff['hotnessLevel'],
    signals: raw.signals,
    reasons: raw.reasons || [],
    outcome: raw.outcome,
    sentiment: raw.sentiment,
    strategyType: raw.strategy_type,
    conversationSummary: raw.conversation_summary,
    status: raw.status as Handoff['status'],
    assignedAgentId: raw.assigned_agent_id,
    assignedAgentName: raw.assigned_agent_name || null,
    createdAt: raw.timestamp,
    claimExpiresAt: raw.claim_expires_at,
    claimedAt: raw.claimed_at || null,
  };

  return {
    type: typeMap[raw.alert_type] || 'new_handoff',
    handoff,
  };
};

/** Minimal EventSource surface this module uses — lets tests substitute a fake. */
export interface EventSourceLike {
  addEventListener(type: string, listener: (event: MessageEvent) => void): void;
  close(): void;
  onerror: ((event: Event) => void) | null;
}

export interface HandoffAlertStreamDeps {
  /** Mint a short-lived opaque stream token (Bearer-authed POST in production). */
  mintStreamToken: (organizationId: string, agentId?: string) => Promise<{ stream_token: string }>;
  /** Base URL of the remi service that serves the SSE endpoint. */
  baseUrl: string;
  /** EventSource factory; defaults to the global `EventSource`. Overridable for tests. */
  createEventSource?: (url: string) => EventSourceLike;
}

/**
 * Open the handoff-alert SSE stream and return a synchronous unsubscribe.
 *
 * The token is minted asynchronously, but the returned unsubscribe is available
 * immediately: if it runs before the mint resolves, the `cancelled` guard stops
 * the EventSource from ever being created. That is the lifecycle guarantee the
 * caller (useHandoffAlerts) relies on to avoid a ghost connection after unmount.
 */
export const openHandoffAlertStream = (
  organizationId: string,
  onHandoff: (event: HandoffSSEEvent) => void,
  onError: ((error: Event) => void) | undefined,
  onConnected: (() => void) | undefined,
  agentId: string | undefined,
  deps: HandoffAlertStreamDeps
): (() => void) => {
  const makeEventSource =
    deps.createEventSource ?? ((url: string) => new EventSource(url) as unknown as EventSourceLike);

  let cancelled = false;
  let eventSource: EventSourceLike | null = null;

  deps
    .mintStreamToken(organizationId, agentId)
    .then(({ stream_token }) => {
      // Unsubscribed while the token was minting — never open the stream.
      if (cancelled) return;

      const params = new URLSearchParams({
        organization_id: organizationId,
        stream_token,
      });
      const es = makeEventSource(`${deps.baseUrl}/api/v1/mobile-handoff/alerts?${params.toString()}`);
      eventSource = es;

      es.addEventListener('open', () => {
        onConnected?.();
      });

      es.addEventListener('handoff', (event) => {
        try {
          const raw = JSON.parse(event.data) as RawSSEHandoff;
          console.log('[SSE] Handoff:', raw.alert_type, raw.contact_name, raw.hotness_level);
          const transformed = transformSSEToHandoff(raw);
          if (typeof onHandoff === 'function') {
            onHandoff(transformed);
          } else {
            console.warn('[SSE] onHandoff callback is not a function');
          }
        } catch (error) {
          console.error('[SSE] Error parsing handoff event:', error);
        }
      });

      es.addEventListener('ping', () => {
        // Ping received - connection is alive
      });

      es.onerror = (error) => {
        onError?.(error);
      };
    })
    .catch((error) => {
      // Mint failed (auth / Redis down / network). Surface it so the caller's
      // reconnect logic (useHandoffAlerts) retries with a fresh token.
      console.error('[SSE] Failed to mint handoff-alert stream token:', error);
      if (!cancelled) onError?.(new Event('error'));
    });

  // Cleanup — synchronous contract. If called before the token resolves, the
  // cancelled flag stops the stream from ever opening.
  return () => {
    cancelled = true;
    eventSource?.close();
  };
};
